import React, { useEffect, useState } from "react";
import styled, { css } from "styled-components";
import buildClassName from "../../../utils/buildClassName";
/**
 * Base Height Based On Which Type Of AV You Want To Use
 */
const dimHeight = {
  full: "calc(100% - 52px)",
  receipt: "59.65%",
  filter: "41.73%",
  trade: "39.41%",
  wallet: "80.0123%",
  confirm: "30.0123%",
  seven: "70%",
  reallyFull: "100%",
};
/**
 * Base Interface For Both Styled Main AV And Ui Component
 */
interface IBase {
  bgColor?: string;
  height?: string;
  dim?:
    | "full"
    | "receipt"
    | "filter"
    | "trade"
    | "wallet"
    | "confirm"
    | "seven"
    | "reallyFull";
  roundedTop?: boolean;
}
/**
 * Styled Activity View Panel Interface
 */
interface StyledActivityPanelProps extends IBase {
  isActive?: boolean;
  maxHeight?: string;
}
/**
 * Activity Panel Main Style
 */
const ActivityPanel = styled.div<StyledActivityPanelProps>`
  position: absolute;
  bottom: 0;
  right: 0;
  height: 0;
  overflow: hidden;
  width: 100%;
  z-index: 2;
  transition: height 0.3s ease-in 150ms;
  border-top-left-radius: ${(props) => (props.roundedTop ? "32px" : "0")};
  border-top-right-radius: ${(props) => (props.roundedTop ? "32px" : "0")};
  ${(p) =>
    p.isActive &&
    css`
      height: ${() => {
        let height = "0px";
        if (p.dim) {
          height = dimHeight[p.dim];
        } else if (p.height) {
          height = p.height;
        }
        return height;
      }};
      overflow: auto;
    `};
  max-height: ${(props) => (props.maxHeight ? props.maxHeight : undefined)};
  background-color: ${(props) => (theme: any) => {
    return props.bgColor ? theme.theme[props.bgColor] : theme.theme.Light6;
  }};
`;
/**
 * Activity View BLur Page
 */
const ActivityViewBlur = styled.div<StyledActivityPanelProps>`
  position: absolute;
  width: 100%;
  height: 0;
  top: 0;
  right: 0;
  background-color: #00000050;
  z-index: 2;
  ${(p) =>
    p.isActive &&
    css`
      height: 100%;
    `};
`;
/**
 * Main Ui Component For Activity View
 */
interface IActivityView extends IBase {
  show?: boolean;
  children?: React.ReactNode;
  onClose: () => void;
  showBlur?: boolean;
  customId?: string;
  maxHeight?: string;
}

/**
 * Main Activity View Ui Component
 * @param dim
 * @param height
 * @param roundedTop
 * @param show
 * @param showBlur
 * @param children
 * @param onClose
 * @constructor
 */
export const ActivityView: React.FC<IActivityView> = ({
  dim = "full",
  height,
  roundedTop = false,
  show = false,
  showBlur = true,
  children,
  onClose,
  customId = "activity-panel-wrapper",
  maxHeight,
}) => {
  const [active, setActive] = useState(false);
  /**
   * Close Activity And Notify Parent
   */
  const closeThis = () => {
    onClose();
    setActive(false);
  };
  /**
   * Watch Parent To Emit Show AV
   */
  useEffect(() => {
    setActive(show);
  }, [show]);
  return (
    <>
      {showBlur && <ActivityViewBlur isActive={active} onClick={closeThis} />}
      <ActivityPanel
        dim={dim}
        roundedTop={roundedTop}
        height={height}
        isActive={active}
        id={customId}
        maxHeight={maxHeight}
        className={buildClassName(active && "active")}
      >
        {children}
      </ActivityPanel>
    </>
  );
};
